<template>
    <div>
        <v-alert
            v-if="timeDifferenceFromNow(codeExpiry)"
            type="warning"
            variant="tonal"
            density="compact"
            class="mb-3 w-100"
        >
            Validation code already sent to {{ emailToBeValidated }} and expires in
            {{ timeDifferenceFromNow(codeExpiry) }}
            <br />
            <v-btn
                size="small"
                variant="text"
                style="text-transform: inherit"
                color="black"
                class="pa-0"
                @click="resetEmailProcess"
            >
                <v-icon left class="me-2">mdi-refresh</v-icon>
                Restart new email process
            </v-btn>
        </v-alert>

        <v-form ref="emailForm" v-model="validEmailForm" lazy-validation>
            <p class="text-body-2 mb-1">Email</p>
            <v-text-field
                v-if="!timeDifferenceFromNow(codeExpiry)"
                v-model="email"
                :placeholder="currentEmail"
                required
                variant="outlined"
                density="compact"
                maxlength="200"
                counter
                class="w-100"
                persistent-placeholder
                :rules="rules.emailRules"
                :disabled="emailVerificationSent"
            ></v-text-field>

            <ButtonComponent
                v-if="!emailVerificationSent && !timeDifferenceFromNow(codeExpiry)"
                :disabled="!validEmailForm"
                style="text-transform: inherit"
                color="primary"
                @click="sendValidationCode"
            >
                {{ $t('user_settings.change_email.send_code_button') }}
            </ButtonComponent>

            <div v-if="emailVerificationSent || timeDifferenceFromNow(codeExpiry)" class="mt-5">
                <v-text-field
                    v-model="verificationCode"
                    type="text"
                    class="mb-3 w-100"
                    required
                    maxlength="6"
                    :label="$t('user_settings.change_email.enter_code')"
                    counter
                    variant="outlined"
                    @keydown.enter.prevent="validateCode"
                ></v-text-field>

                <v-btn
                    size="large"
                    style="width: 200px !important"
                    :loading="loadingCodeValidation"
                    :disabled="!validEmailForm"
                    color="success"
                    @click="validateCode"
                >
                    {{ $t('user_settings.change_email.confirm_code_button') }}
                </v-btn>

                <v-btn
                    size="medium"
                    variant="text"
                    style="text-transform: inherit"
                    class="ml-5"
                    color="secondary"
                    :loading="loadingResendCode"
                    @click="resendCode"
                >
                    <v-icon left class="me-2">mdi-refresh</v-icon>
                    {{ $t('user_settings.change_email.resend_code_button') }}
                </v-btn>
            </div>
        </v-form>
    </div>
</template>

<script>
import { useUserStore } from '@/stores/user';
import { toast } from 'vue3-toastify';
import ButtonComponent from '@/components/utils/buttons/ButtonComponent.vue';
import { defaultErrorHandling } from '@/assets/js/utils';

export default {
    name: 'ChangeEmail',
    components: {
        ButtonComponent,
    },
    data: () => ({
        user_store: useUserStore(),
        email: '',
        verificationCode: '',
        validEmailForm: false,
        emailVerificationSent: false,
        emailToBeValidated: undefined,
        codeExpiry: undefined,
        loadingResendCode: false,
        loadingCodeValidation: false,
        rules: {
            required: (value) => !!value || 'Must be specified.',
            emailRules: [
                (v) => !!v || 'Please provide a valid email',
                (v) => /.+@.+\..+/.test(v) || 'Please provide a valid email',
            ],
        },
    }),
    computed: {
        currentEmail() {
            return this.user_store.get_user.email;
        },
    },
    mounted() {
        this.emailToBeValidated = localStorage.getItem('emailToBeValidated');
        this.codeExpiry = new Date(localStorage.getItem('codeExpiry'));
    },
    methods: {
        resetEmailProcess() {
            this.emailVerificationSent = false;
            this.emailToBeValidated = undefined;
            this.codeExpiry = undefined;
            localStorage.removeItem('emailToBeValidated');
            localStorage.removeItem('codeExpiry');
        },
        timeDifferenceFromNow(timestamp) {
            if (!timestamp) {
                return false;
            }
            const now = new Date();

            const twoHoursInMs = 2 * 60 * 60 * 1000;
            const timeDiff = now - timestamp;

            if (timeDiff < twoHoursInMs) {
                const remainingTime = twoHoursInMs - timeDiff;

                const hours = Math.floor(remainingTime / (1000 * 60 * 60));
                const minutes = Math.floor((remainingTime % (1000 * 60 * 60)) / (1000 * 60));

                return `${hours} hour(s) and ${minutes} minute(s)`;
            } else {
                return false;
            }
        },
        sendValidationCode() {
            if (this.timeDifferenceFromNow(this.codeExpiry) === false) {
                localStorage.setItem('emailToBeValidated', this.email);
                localStorage.setItem('codeExpiry', new Date().toISOString());
            } else {
                toast.error(this.$t('alerts.error.please_wait_for_code_to_expire'));
                return;
            }

            this.loadingCodeValidation = true;
            this.user_store
                .change_email(this.email)
                .then(() => {
                    toast.success(this.$t('alerts.success.verfifcation_code_sent'));
                })
                .catch(defaultErrorHandling())
                .finally(() => {
                    this.loadingCodeValidation = false;
                });

            this.emailVerificationSent = true;
        },
        async validateCode() {
            if (this.verificationCode.length === 6) {
                this.user_store
                    .validate_code(
                        this.verificationCode,
                        this.email,
                        this.user_store.get_user.email,
                        'change_email',
                    )
                    .then(() => {
                        toast.success(this.$t('alerts.success.email_changed'));
                        localStorage.removeItem('emailToBeValidated');
                        localStorage.removeItem('codeExpiry');
                    })
                    .catch(defaultErrorHandling());
            } else {
                toast.error(this.$t('alerts.error.please_enter_valid_code'));
            }
        },
        resendCode() {
            localStorage.setItem('codeExpiry', new Date().toISOString());
            this.loadingResendCode = true;
            this.user_store
                .resend_code(this.email, 'change_email')
                .then(() => {
                    toast.success(this.$t('alerts.success.verfifcation_code_sent'));
                })
                .catch(defaultErrorHandling())
                .finally(() => {
                    this.loadingResendCode = false;
                });
        },
    },
};
</script>

<style></style>
